import type {
  GetExternalAccountLinkedAccountsQuery,
  GetExternalAccountLinkedAccountsQueryVariables,
  GetExternalAccountTxsQuery,
  GetExternalAccountTxsQueryVariables,
} from 'hooks/customerApi';
import type { AnyVariables, UseQueryExecute, UseQueryState } from 'urql';
import type { DefaultParams, Params } from 'wouter';
import { ExternalAccountSummary } from 'components/link/ExternalAccountSummary';
import { LinkedLedgerAccountSelector } from 'components/link/LinkedLedgerAccountSelector';
import { TxsTable } from 'components/link/TxsTable/TxsTableV1';
import { type LinkedLedgerAccount, type TxNode } from 'components/link/types';
import { TableLoadError } from 'components/TableLoadError/TableLoadError';
import {
  GetExternalAccountLinkedAccountsDocument,
  GetExternalAccountTxsDocument,
  useGetExternalAccountSummaryQuery,
} from 'hooks/customerApi';
import { useCustomerApiContext } from 'hooks/useCustomerApiContext';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useHasWorkspacePermission } from 'hooks/useHasWorkspacePermission';
import React, { useCallback, useMemo, useState, useTransition } from 'react';
import { getResultOrError } from 'utils/errors';
import { makePath } from 'utils/routes';
import { usePaginatedQuery } from 'utils/urql';
import { useLocation } from 'wouter';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider';

import { Permissions } from '@fragment/authorization-utils/permissions';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { TextButton } from '@fragment/ui/src/components/Button/TextButton/TextButton';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { Icon } from '@fragment/ui/src/components/Icon';

import { SettingsModalLayout } from '../SchemasV2/WorkspaceSettings/SettingsModalLayout';
import { SyncCustomTxView } from './SyncCustomTxView';

const ExternalAccountTxsTable = React.memo(
  ({
    ledgerAccountId,
    response,
    reExecuteQuery,
    data,
    paginateNext,
  }: {
    ledgerAccountId: string | undefined;
    response: UseQueryState<GetExternalAccountTxsQuery, AnyVariables>;
    reExecuteQuery: UseQueryExecute;
    data: TxNode[];
    paginateNext: () => void;
  }) => {
    const [isPending, startTransition] = useTransition();

    const loadMore = useCallback(
      () => startTransition(paginateNext),
      [paginateNext]
    );
    const { isError } = getResultOrError(
      response.data?.externalAccount,
      response.error
    );

    const retry = useCallback(
      () => reExecuteQuery({ requestPolicy: 'network-only' }),
      [reExecuteQuery]
    );

    return isError ? (
      <TableLoadError
        entityColumnName="Transaction"
        {...{
          error: response.error,
          retry,
          fetching: response.fetching,
        }}
      />
    ) : (
      <TxsTable
        ledgerAccountId={ledgerAccountId}
        data={data}
        selected={undefined}
        getPath={undefined}
        hasNextPage={response.data?.externalAccount?.txs.pageInfo.hasNextPage}
        loadMore={loadMore}
        isLoading={isPending}
      />
    );
  }
);

type Props<T extends DefaultParams> = {
  params: Params<T>;
};

export const ExternalAccountDetailView = <T extends DefaultParams>({
  params: { linkId, accountId },
}: Props<T>) => {
  const { context, token } = useCustomerApiContext();
  const workspaceId = useWorkspaceId();
  const canAddTx = useHasWorkspacePermission(Permissions.link.sync);
  const [, navigate] = useLocation();
  const extAcctSummaryVariables = useMemo(
    () => ({
      id: accountId,
    }),
    [accountId]
  );
  const [queryResult, reExecuteQuery] = useGetExternalAccountSummaryQuery({
    context,
    pause: !context.token,
    variables: extAcctSummaryVariables,
  });
  const { isError, result: externalAccount } = getResultOrError(
    queryResult.data?.externalAccount,
    queryResult.error
  );

  const retry = useCallback(
    () => reExecuteQuery({ requestPolicy: 'network-only' }),
    [reExecuteQuery]
  );
  useDocumentTitle(
    queryResult.error || queryResult.fetching
      ? 'External Account'
      : `External Account - ${externalAccount?.name}`
  );

  const [selected, setSelected] = useState<LinkedLedgerAccount>();
  const extAcctLinkedAccountVariables = useMemo(
    () => ({
      id: accountId,
    }),
    [accountId]
  );
  const {
    response: listAccountsResponse,
    reExecuteQuery: reExecuteAccountsQuery,
    data,
    paginateNext,
  } = usePaginatedQuery<
    LinkedLedgerAccount,
    GetExternalAccountLinkedAccountsQuery,
    GetExternalAccountLinkedAccountsQueryVariables
  >({
    getConnection: ({ externalAccount: extAcct }) => extAcct?.ledgerAccounts,
    queryDocument: GetExternalAccountLinkedAccountsDocument,
    queryOptions: {
      context,
      pause: !context.token,
    },
    variables: extAcctLinkedAccountVariables,
  });
  const [isPending, startTransition] = useTransition();
  const loadMore = useCallback(() => {
    startTransition(paginateNext);
  }, [paginateNext]);

  const { isError: isLinkedAccountError } = getResultOrError(
    listAccountsResponse.data?.externalAccount,
    listAccountsResponse.error
  );

  const getPath = useCallback(
    (account: LinkedLedgerAccount) =>
      makePath(
        [
          'w',
          workspaceId,
          'projects',
          ...(account.ledger.schema?.key
            ? ['schema', account.ledger.schema.key]
            : []),
          account.ledgerId,
          'accounts',
          account.id,
        ],
        {
          trailingSlash: true,
          absolute: true,
        }
      ),
    [workspaceId]
  );

  const retryListAccounts = useCallback(
    () => reExecuteAccountsQuery({ requestPolicy: 'network-only' }),
    [reExecuteAccountsQuery]
  );

  // Typescript doesn't infer that this can be undefined, so I have to help it along.
  const selectedAccount: LinkedLedgerAccount | undefined =
    selected ?? (data[0] as LinkedLedgerAccount | undefined);
  const accountSelector = isLinkedAccountError ? (
    <TableLoadError
      entityColumnName="Account"
      {...{
        error: listAccountsResponse.error,
        retry: retryListAccounts,
        fetching: listAccountsResponse.fetching,
      }}
    />
  ) : (
    <LinkedLedgerAccountSelector
      accounts={data}
      setSelected={setSelected}
      selected={selectedAccount}
      loadMore={
        listAccountsResponse.data?.externalAccount?.ledgerAccounts.pageInfo
          .hasNextPage
          ? loadMore
          : undefined
      }
      fetching={isPending}
      getPath={getPath}
    />
  );

  const [isAddingTx, setIsAddingTx] = useState(false);

  const {
    response: responseTxTable,
    reExecuteQuery: reExecuteQueryTxTable,
    data: dataTxTable,
    paginateNext: paginateNextTxTable,
  } = usePaginatedQuery<
    TxNode,
    GetExternalAccountTxsQuery,
    GetExternalAccountTxsQueryVariables
  >({
    getConnection: ({ externalAccount: externalAccountTx }) =>
      externalAccountTx?.txs,
    queryDocument: GetExternalAccountTxsDocument,
    queryOptions: {
      context,
      pause: !token,
    },
    variables: extAcctLinkedAccountVariables,
  });

  const navigateToLink = () => navigate(makePath(['links', 'id', linkId]));

  if (isError) {
    return (
      <SettingsModalLayout
        title="Failed to load External Account"
        onBackButtonClick={navigateToLink}
      >
        <div className="flex justify-between gap-2">
          <ErrorText>Error fetching External Account details</ErrorText>
          <TextButton inverted onClick={retry}>
            Retry <Icon type="right" />
          </TextButton>
        </div>
      </SettingsModalLayout>
    );
  }

  if (isAddingTx) {
    return (
      <SyncCustomTxView
        externalAccount={externalAccount}
        refresh={() =>
          setTimeout(() => {
            retry();
            reExecuteQueryTxTable({ requestPolicy: 'network-only' });
          }, 1000)
        }
        onClose={() => setIsAddingTx(false)}
        linkId={linkId}
      />
    );
  }

  const shouldRenderAddTxCTA =
    canAddTx && externalAccount.link.__typename === 'CustomLink';

  return (
    <SettingsModalLayout
      title={`${externalAccount.link.name} / ${externalAccount.name}`}
      onBackButtonClick={navigateToLink}
      topButtonSection={
        shouldRenderAddTxCTA && (
          <PrimaryButtonV2
            onClick={() => setIsAddingTx(true)}
            secondaryIcon="+"
          >
            Add Tx
          </PrimaryButtonV2>
        )
      }
    >
      <ExternalAccountSummary
        externalAccountId={externalAccount.externalId}
        externalAccountName={externalAccount.name}
        currency={externalAccount.currency}
        linkType={externalAccount.link.__typename}
        accountSelector={accountSelector}
        currencyMode={externalAccount.currencyMode}
      />
      <ExternalAccountTxsTable
        ledgerAccountId={selectedAccount?.id}
        response={responseTxTable}
        reExecuteQuery={reExecuteQueryTxTable}
        data={dataTxTable}
        paginateNext={paginateNextTxTable}
      />
    </SettingsModalLayout>
  );
};
